/**
 * Phase 11+ requirement — help button on every page.
 *
 * Renders a "?" affordance in the surface header. Click opens a popover
 * with the surface's "what is this?" + "how to use it" content. Content
 * lives in the surface registry (registry.tsx `help` field) so it stays
 * close to the surface definition and the ⌘K palette can also surface it.
 *
 * Honesty rail: no help content invented. Surfaces that don't yet have
 * a `help` entry render a small dimmed "?" with a tooltip pointing to
 * the plan-file so the gap is visible rather than papered over.
 */

import { useEffect, useRef, useState } from 'react';
import { findSurface } from './surfaces/registry';

interface HelpButtonProps {
  /** Surface id from the registry — looks up `help` content. */
  surfaceId: string;
}

export function HelpButton({ surfaceId }: HelpButtonProps) {
  const surface = findSurface(surfaceId);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const help = surface?.help;

  return (
    <span style={{ position: 'relative' as const, display: 'inline-block' }} ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={help ? 'What is this page + how to use it' : 'Help content not yet authored — see docs/biomax-pacc-decision-grade-plan.md'}
        aria-label="Open help for this surface"
        aria-expanded={open}
        style={{
          display: 'inline-grid', placeItems: 'center',
          width: 22, height: 22, borderRadius: '50%',
          fontFamily: 'var(--bm-font-mono)', fontSize: 12, fontWeight: 700,
          color: help ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-text-subtle)',
          background: 'transparent',
          border: `1px solid ${help ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-border)'}`,
          cursor: 'pointer',
          opacity: help ? 1 : 0.5,
        }}
      >?</button>
      {open && (
        <div
          role="dialog"
          aria-label={`Help: ${surface?.label ?? surfaceId}`}
          style={{
            position: 'absolute' as const, top: 'calc(100% + 6px)', right: 0, zIndex: 100,
            width: 'min(420px, 90vw)',
            padding: 14,
            background: 'var(--bm-bg-panel, var(--bm-bg))',
            border: '1px solid var(--bm-border)',
            borderRadius: 'var(--bm-radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text)',
            textAlign: 'left' as const,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 10, marginBottom: 8,
          }}>
            <span style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
              letterSpacing: '.5px', textTransform: 'uppercase' as const,
              color: 'var(--bm-accent, #6ea3ff)',
            }}>What is this · how to use it</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close help"
              style={{
                padding: 0, width: 18, height: 18,
                fontFamily: 'var(--bm-font-mono)', fontSize: 12,
                color: 'var(--bm-text-subtle)', background: 'transparent',
                border: 'none', cursor: 'pointer',
              }}
            >✕</button>
          </div>
          {help ? (
            <>
              <p style={{ margin: '0 0 10px', color: 'var(--bm-text)' }}>{help.what}</p>
              <div style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
                letterSpacing: '.5px', textTransform: 'uppercase' as const,
                color: 'var(--bm-text-subtle)', marginBottom: 5,
              }}>How to use it</div>
              <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--bm-text-muted)' }}>
                {help.howTo.map((step, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{step}</li>
                ))}
              </ol>
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--bm-text-subtle)', fontStyle: 'italic' as const }}>
              Help content not yet authored for this surface. Tracked in
              <code style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 11,
                background: 'var(--bm-bg-subtle)',
                border: '1px solid var(--bm-border)',
                padding: '1px 5px', borderRadius: 3, marginLeft: 4,
              }}>docs/biomax-pacc-decision-grade-plan.md</code>.
            </p>
          )}
        </div>
      )}
    </span>
  );
}
